import type { ReactNode } from 'react';

export interface SectionProps {
  /** Optional uppercase section header text shown above the rows.
   * iOS Settings convention — small, gray, all-caps. */
  title?: ReactNode;
  /** Optional helper text shown below the rows in a smaller, lighter
   * style. Used for explanations like "Used to suggest content
   * preferences". */
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * Grouped section of list rows with optional title above and helper
 * text below — the iOS Settings pattern. Compose with `<List>` and
 * `<ListRow>`:
 *
 * @example
 * <Section title="Account" footer="Used to sign you in across devices">
 *   <List>
 *     <ListRow title="Email" meta="alice@example.com" chevron onClick={...} />
 *     <ListRow title="Phone" meta="+1 555 0100"      chevron onClick={...} />
 *   </List>
 * </Section>
 */
export function Section({ title, footer, children }: SectionProps) {
  return (
    <div className="mt-6 first:mt-0">
      {title && (
        <div className="px-4 mb-1.5 text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
          {title}
        </div>
      )}
      {children}
      {footer && (
        <div className="px-4 mt-1.5 text-[11px] text-gray-400 dark:text-gray-500 leading-relaxed">
          {footer}
        </div>
      )}
    </div>
  );
}
